<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiSheet from '@/components/pos-v2/ui/PosUiSheet.vue';
  import type { TSheetHeight } from '@/components/pos-v2/ui/pos-ui-sheet.utils';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import {
    hasPopularData,
    popularCardVisible,
  } from '@/domain/catalog/popular-showcase';
  import V2CatalogPopularList from './V2CatalogPopularList.vue';

  /**
   * «Lo más pedido» (Storefront V2 D-ADM-4): a slim card next to «Descuentos»
   * with the storefront's switch for the most ordered products. Shown while
   * some branch has enough orders to rank them, or while the owner has the
   * switch off. Branches come from the store's `/full` data, never from the
   * role-filtered branch list, so a branch-limited member sees the same card.
   * On phones the explanation opens in a sheet. While the storefront shows a
   * ranking, the sheet also holds its list (V2CatalogPopularList), opened by
   * «Ver lista» on wider screens (owner 2026-11-03).
   */
  const { data, access, online, actions, layout } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const restaurant = data.restaurant;
  const phone = computed(() => layout.tier.value === 'phone');
  const sheetOpen = ref(false);
  const switching = ref(false);

  const shown = computed(
    () => restaurant.value?.settings?.showPopular !== false
  );
  const hasData = computed(() => hasPopularData(restaurant.value?.branches));
  /** The list is what the storefront shows: the switch on and a ranking. */
  const listShown = computed(() => shown.value && hasData.value);
  const sheetHeight = ref<TSheetHeight>('lg');

  /** A phone gets the list nearly full height; wider screens hold its 8 rows at most in a medium sheet. */
  function openSheet() {
    if (!listShown.value) sheetHeight.value = 'sm';
    else sheetHeight.value = phone.value ? 'lg' : 'md';
    sheetOpen.value = true;
  }
  // The sheet unmounts when the list goes away on a wide screen (the switch
  // turned off elsewhere): it must not come back open with the list.
  watch(
    () => phone.value || listShown.value,
    (mounted) => {
      if (!mounted) sheetOpen.value = false;
    }
  );
  const storeId = computed(() => restaurant.value?.id ?? null);
  const rule = computed(() =>
    popularCardVisible({
      branches: restaurant.value?.branches,
      showPopular: restaurant.value?.settings?.showPopular,
    })
  );

  /**
   * Latched while the page shows one store: turning the switch on for a store
   * without data keeps the card (with its note) under the owner's finger; the
   * next visit applies the rule again.
   */
  const visible = ref(false);
  watch(
    [storeId, rule],
    ([id, ruleNow], [previousId]) => {
      if (id !== previousId) visible.value = ruleNow;
      else if (ruleNow) visible.value = true;
    },
    { immediate: true }
  );

  const hint = computed(() =>
    t(shown.value ? 'menuPage.popular.onHint' : 'menuPage.popular.offHint')
  );
  const switchTitle = computed(() => {
    if (!access.value.canPopularSwitch)
      return t('menuPage.discounts.needsSettings');
    return online.value ? undefined : t('menuPage.errors.offline');
  });

  async function toggle(show: boolean) {
    if (switching.value) return;
    switching.value = true;
    try {
      await actions.setShowPopular(show);
    } finally {
      switching.value = false;
    }
  }
</script>

<template>
  <div v-if="visible" class="v2-catalog-popular-card">
    <i class="mdi mdi-trending-up card-icon" aria-hidden="true" />
    <button
      v-if="phone"
      type="button"
      class="card-text is-button"
      @click="openSheet"
    >
      <span class="card-title">{{ t('menuPage.popular.title') }}</span>
    </button>
    <div v-else class="card-text">
      <span class="card-line">
        <span class="card-title">{{ t('menuPage.popular.title') }}</span>
        · {{ hint }}
      </span>
      <span v-if="!hasData" class="card-note">
        {{ t('menuPage.popular.noData') }}
      </span>
    </div>
    <span v-if="!phone && listShown" class="card-list">
      <PosUiButton color="neutral" variant="outline" @click="openSheet">{{
        t('menuPage.popular.viewList')
      }}</PosUiButton>
    </span>
    <span class="card-switch" :title="switchTitle">
      <PosUiSwitch
        :value="shown"
        :label="t('menuPage.discounts.show')"
        :disabled="!access.canPopularSwitch || !online || switching"
        @input="toggle"
      />
    </span>

    <PosUiSheet
      v-if="phone || listShown"
      :value="sheetOpen"
      :title="t('menuPage.popular.title')"
      :height.sync="sheetHeight"
      :max-width="phone ? undefined : 560"
      @input="sheetOpen = $event"
    >
      <div class="popular-sheet">
        <h2 class="sheet-title">{{ t('menuPage.popular.title') }}</h2>
        <template v-if="phone">
          <p class="sheet-text">{{ t('menuPage.popular.explain') }}</p>
          <p class="sheet-text">{{ hint }}</p>
          <p v-if="!hasData" class="sheet-text">
            {{ t('menuPage.popular.noData') }}
          </p>
        </template>
        <V2CatalogPopularList
          v-if="listShown"
          class="sheet-list"
          :open="sheetOpen"
        />
      </div>
    </PosUiSheet>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-popular-card {
    @apply tw-mb-3 tw-flex tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-py-2 tw-pl-4 tw-pr-2;

    .card-icon {
      @apply tw-mr-3 tw-flex-none tw-text-xl tw-text-neutral-500;
    }

    .card-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-text-sm tw-leading-5 tw-text-neutral-600;

      &.is-button {
        @apply tw-min-h-[44px] tw-cursor-pointer tw-flex-row tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-left;
      }

      .card-title {
        @apply tw-mr-1 tw-font-semibold tw-text-neutral-900;
      }

      .card-note {
        @apply tw-mt-0.5 tw-text-xs tw-text-neutral-500;
      }
    }

    .card-list {
      @apply tw-ml-3 tw-flex-none;
    }

    .card-switch {
      @apply tw-ml-3 tw-flex-none;
    }

    .popular-sheet {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-4;

      .sheet-list {
        @apply tw-mt-3;
      }

      .sheet-title {
        @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
      }

      .sheet-text {
        @apply tw-mb-3 tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-neutral-600;
      }
    }
  }
</style>
